import React, { useState, useEffect } from 'react';
import { Form, Button, Spinner } from 'react-bootstrap';
import formService from '../services/formService';

// Renders a 'file' type form field (e.g. inspection photo): uploads immediately on
// selection and stores the returned filename as the field's value, exactly like
// every other field type stores its typed/selected value. Shared between
// DynamicForm.js (new submission) and EditSubmission.js (sent-back resubmission).
const PhotoFieldUpload = ({ field, value, onChange, disabled }) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  useEffect(() => {
    let objectUrl;
    let cancelled = false;

    if (value) {
      formService.getFieldFile(value)
        .then((blob) => {
          if (cancelled) return;
          objectUrl = URL.createObjectURL(blob);
          setPreviewUrl(objectUrl);
        })
        .catch(() => { if (!cancelled) setPreviewUrl(null); });
    } else {
      setPreviewUrl(null);
    }

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [value]);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadError('');
    setUploading(true);
    try {
      const response = await formService.uploadFieldFile(file);
      if (response.success) {
        onChange(response.filename);
      } else {
        setUploadError(response.message || 'Upload failed');
      }
    } catch (err) {
      setUploadError(err.message || 'Failed to upload photo');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const acceptTypes = (() => {
    const opts = typeof field.field_options === 'string'
      ? (() => { try { return JSON.parse(field.field_options); } catch { return null; } })()
      : field.field_options;
    return (opts && opts.accept) ? opts.accept.join(',') : 'image/*';
  })();

  return (
    <Form.Group className="mb-3">
      <Form.Label>
        {field.field_label}
        {field.is_required && <span className="text-danger"> *</span>}
      </Form.Label>
      <div className="d-flex align-items-start gap-3">
        <div
          className="border rounded d-flex align-items-center justify-content-center bg-light"
          style={{ width: 90, height: 90, overflow: 'hidden', flexShrink: 0 }}
        >
          {previewUrl ? (
            <img
              src={previewUrl}
              alt={field.field_label}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <i className="fas fa-image text-muted" style={{ fontSize: '1.5rem' }}></i>
          )}
        </div>
        <div className="flex-grow-1">
          <Form.Control
            type="file"
            accept={acceptTypes}
            onChange={handleFileChange}
            disabled={disabled || uploading}
          />
          {uploading && (
            <Form.Text className="text-muted d-block mt-1">
              <Spinner animation="border" size="sm" className="me-1" />Uploading...
            </Form.Text>
          )}
          {uploadError && <Form.Text className="text-danger d-block mt-1">{uploadError}</Form.Text>}
          {value && !uploading && !uploadError && (
            <Button variant="link" size="sm" className="text-danger p-0 mt-1" onClick={() => onChange('')}>
              Remove photo
            </Button>
          )}
          {field.help_text && <Form.Text className="text-muted d-block">{field.help_text}</Form.Text>}
        </div>
      </div>
    </Form.Group>
  );
};

// Read-only thumbnail for a 'file' type field's stored value — used on review/
// view-only pages where there's no upload control, just the uploaded image.
export const PhotoFieldPreview = ({ filename, alt, size = 120 }) => {
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    let objectUrl;
    let cancelled = false;

    if (filename) {
      formService.getFieldFile(filename)
        .then((blob) => {
          if (cancelled) return;
          objectUrl = URL.createObjectURL(blob);
          setPreviewUrl(objectUrl);
        })
        .catch(() => { if (!cancelled) setPreviewUrl(null); });
    } else {
      setPreviewUrl(null);
    }

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [filename]);

  if (!filename) {
    return <span className="text-muted fst-italic">Not provided</span>;
  }

  return (
    <div
      className="border rounded d-flex align-items-center justify-content-center bg-light"
      style={{ width: size, height: size, overflow: 'hidden' }}
    >
      {previewUrl ? (
        <img src={previewUrl} alt={alt || 'Photo'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        <i className="fas fa-image text-muted" style={{ fontSize: '1.5rem' }}></i>
      )}
    </div>
  );
};

export default PhotoFieldUpload;
